import React, { useState, useCallback, useRef } from "react";
import { Sparkles, ShieldAlert, ArrowRight, ArrowLeft, User, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from "react-i18next";

type MicroStep = "name" | "about";

export const MICRO_STEPS: MicroStep[] = ["name", "about"];

/**
 * Exact age on `today` for a YYYY-MM-DD birth date, or null when the parts
 * don't form a real date. Mirrors the server gate in PATCH /api/auth/user —
 * the earliest selectable birth year is still 18 years back, so a later
 * month/day in that year lands on 17.
 */
export function computeAgeFromParts(
  year: string,
  month: string,
  day: string,
  today: Date = new Date(),
): number | null {
  const y = parseInt(year, 10);
  const m = parseInt(month, 10);
  const d = parseInt(day, 10);
  if (!y || !m || !d) return null;
  let age = today.getFullYear() - y;
  if (today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d)) age--;
  return age;
}

interface ProfileStepProps {
  preferredName: string;
  onPreferredNameChange: (value: string) => void;
  gender: string;
  onGenderChange: (value: string) => void;
  dateOfBirth: string;
  onDateOfBirthChange: (value: string) => void;
  onSave: () => void;
  onSkip: () => void;
  savePending: boolean;
}

export const ProfileStep = React.memo(function ProfileStep({
  preferredName,
  onPreferredNameChange,
  gender,
  onGenderChange,
  dateOfBirth,
  onDateOfBirthChange,
  onSave,
  onSkip,
  savePending,
}: ProfileStepProps) {
  const { t } = useTranslation();
  const [microStep, setMicroStep] = useState<MicroStep>("name");
  const [direction, setDirection] = useState<"forward" | "backward">("forward");
  const [animating, setAnimating] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Progressive DOB state
  const [ageGroup, setAgeGroup] = useState<"under18" | "adult" | null>(null);
  const [birthYear, setBirthYear] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthDay, setBirthDay] = useState("");
  const dobInitialized = useRef(false);

  // Initialize DOB sub-fields from existing dateOfBirth prop
  React.useEffect(() => {
    if (dobInitialized.current) return;
    if (dateOfBirth && dateOfBirth.length === 10) {
      dobInitialized.current = true;
      const [y, m, d] = dateOfBirth.split("-");
      setBirthYear(y);
      setBirthMonth(m);
      setBirthDay(d);
      const age = computeAgeFromParts(y, m, d);
      setAgeGroup(age !== null && age < 18 ? "under18" : "adult");
    }
  }, [dateOfBirth]);

  // Compose full date when all parts are set
  React.useEffect(() => {
    if (birthYear && birthMonth && birthDay) {
      const composed = `${birthYear}-${birthMonth}-${birthDay}`;
      if (composed !== dateOfBirth) {
        onDateOfBirthChange(composed);
      }
    } else if (dateOfBirth) {
      onDateOfBirthChange("");
    }
  }, [birthYear, birthMonth, birthDay]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAgeGroupChange = useCallback((group: "under18" | "adult") => {
    setAgeGroup(group);
    setBirthYear("");
    setBirthMonth("");
    setBirthDay("");
    onDateOfBirthChange("");
  }, [onDateOfBirthChange]);

  // Year options filtered by age group
  const currentYear = new Date().getFullYear();
  const yearOptions = React.useMemo(() =>
    Array.from({ length: 83 }, (_, i) => String(currentYear - 18 - i)),
  [currentYear]);

  // Localized month names
  const monthOptions = React.useMemo(() =>
    Array.from({ length: 12 }, (_, i) => ({
      value: String(i + 1).padStart(2, "0"),
      label: new Date(2024, i, 1).toLocaleDateString(undefined, { month: "long" }),
    })),
  []);

  // Day options capped by selected month/year
  const maxDay = birthYear && birthMonth
    ? new Date(parseInt(birthYear), parseInt(birthMonth), 0).getDate()
    : 31;
  const dayOptions = React.useMemo(() =>
    Array.from({ length: maxDay }, (_, i) => String(i + 1).padStart(2, "0")),
  [maxDay]);

  // Cap birthDay if it exceeds maxDay when month changes
  React.useEffect(() => {
    if (birthDay && parseInt(birthDay) > maxDay) {
      setBirthDay(String(maxDay).padStart(2, "0"));
    }
  }, [maxDay, birthDay]);

  const currentIndex = MICRO_STEPS.indexOf(microStep);
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === MICRO_STEPS.length - 1;
  const progress = (currentIndex / (MICRO_STEPS.length - 1)) * 100;

  React.useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const transition = useCallback((nextStep: MicroStep, dir: "forward" | "backward") => {
    if (animating) return;
    setDirection(dir);
    setAnimating(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setMicroStep(nextStep);
      setAnimating(false);
      timerRef.current = null;
    }, 200);
  }, [animating]);

  const goNext = useCallback(() => {
    if (animating) return;
    if (isLast) {
      onSave();
      return;
    }
    const next = MICRO_STEPS[currentIndex + 1];
    transition(next, "forward");
  }, [currentIndex, isLast, onSave, transition, animating]);

  const goBack = useCallback(() => {
    if (isFirst || animating) return;
    const prev = MICRO_STEPS[currentIndex - 1];
    transition(prev, "backward");
  }, [currentIndex, isFirst, transition, animating]);

  const hasCompleteDob = Boolean(birthYear && birthMonth && birthDay);
  // The year list starts 18 years back, so the month/day pickers can still
  // compose a 17-year-old date. Without this check the server 400s on save and
  // the user is stuck on a generic error toast with no way forward.
  const selectedAge = hasCompleteDob ? computeAgeFromParts(birthYear, birthMonth, birthDay) : null;
  const isUnderage = ageGroup === "under18" || (selectedAge !== null && selectedAge < 18);
  const canProceed =
    microStep === "name" ? preferredName.trim().length > 0 : hasCompleteDob && !isUnderage;
  const canSave = Boolean(preferredName.trim()) && hasCompleteDob && !isUnderage;

  const slideClass = animating
    ? direction === "forward"
      ? "opacity-0 translate-x-6"
      : "opacity-0 -translate-x-6"
    : "opacity-100 translate-x-0";

  const stepTitles: Record<MicroStep, { title: string; desc: string; icon: React.ReactNode }> = {
    name: {
      title: t("profileCompletion.welcomeTitle"),
      desc: t("profileCompletion.stepNameDesc", "What should we call you?"),
      icon: <User className="h-8 w-8 text-primary" />,
    },
    about: {
      title: t("profileCompletion.stepAboutTitle", "A bit about you"),
      desc: t("profileCompletion.stepAboutDesc", "Help us personalize your experience"),
      icon: <Sparkles className="h-8 w-8 text-primary" />,
    },
  };

  const currentStepInfo = stepTitles[microStep];

  return (
    <div className="flex flex-col min-h-[340px]">
      <div className="w-full bg-muted/50 rounded-full h-1.5 mb-6 overflow-hidden">
        <div
          className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="flex flex-col items-center text-center mb-6">
        <div className="mb-3">
          {currentStepInfo.icon}
        </div>
        <h2 className="font-serif text-xl font-bold">{currentStepInfo.title}</h2>
        <p className="text-sm text-muted-foreground mt-1">{currentStepInfo.desc}</p>
      </div>

      <div
        ref={contentRef}
        className={`flex-1 transition-all duration-200 ease-out ${slideClass}`}
      >
        {microStep === "name" && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="completion-preferredname">{t("profileCompletion.preferredName")}</Label>
              <p className="text-xs text-muted-foreground mb-2">{t("profileCompletion.preferredNameHint")}</p>
              <Input
                id="completion-preferredname"
                type="text"
                placeholder={t("profileCompletion.preferredNamePlaceholder")}
                value={preferredName}
                onChange={(e) => onPreferredNameChange(e.target.value)}
                maxLength={100}
                autoFocus
                data-testid="input-completion-preferredname"
              />
            </div>
          </div>
        )}

        {microStep === "about" && (
          <div className="space-y-4">
            <div>
              <Label>{t("profileCompletion.genderLabel")}</Label>
              <Select value={gender} onValueChange={onGenderChange}>
                <SelectTrigger data-testid="select-completion-gender">
                  <SelectValue placeholder={t("profileCompletion.genderPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">{t("profileCompletion.genderMale")}</SelectItem>
                  <SelectItem value="female">{t("profileCompletion.genderFemale")}</SelectItem>
                  <SelectItem value="non-binary">{t("profileCompletion.genderNonBinary")}</SelectItem>
                  <SelectItem value="prefer-not-to-say">{t("profileCompletion.genderPreferNot")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{t("profileCompletion.ageGroupQuestion", "How old are you?")}</Label>
              <p className="text-xs text-muted-foreground mb-2">{t("profileCompletion.dobHint")}</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => handleAgeGroupChange("under18")}
                  className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-colors min-h-[44px] text-sm ${
                    ageGroup === "under18"
                      ? "border-primary bg-primary/5 text-primary font-medium"
                      : "border-muted hover:border-muted-foreground/30"
                  }`}
                  data-testid="button-age-under18"
                >
                  {t("profileCompletion.ageGroupUnder18", "Under 18")}
                </button>
                <button
                  type="button"
                  onClick={() => handleAgeGroupChange("adult")}
                  className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-colors min-h-[44px] text-sm ${
                    ageGroup === "adult"
                      ? "border-primary bg-primary/5 text-primary font-medium"
                      : "border-muted hover:border-muted-foreground/30"
                  }`}
                  data-testid="button-age-adult"
                >
                  {t("profileCompletion.ageGroup18Plus", "18 or older")}
                </button>
              </div>
            </div>

            {isUnderage && (
              <div
                className="flex flex-col items-center text-center py-6 space-y-3 animate-in fade-in slide-in-from-top-2 duration-200"
                data-testid="underage-rejection"
              >
                <ShieldAlert className="h-10 w-10 text-muted-foreground" />
                <h3 className="font-serif text-lg font-semibold">
                  {t("profileCompletion.underageTitle", "Troopn is for 18+")}
                </h3>
                <p className="text-sm text-muted-foreground max-w-xs">
                  {t("profileCompletion.underageMessage", "Troopn is for travelers 18 and older. Come back when you're old enough to join the adventure!")}
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setAgeGroup(null);
                    setBirthYear("");
                    setBirthMonth("");
                    setBirthDay("");
                    onDateOfBirthChange("");
                  }}
                  data-testid="button-underage-gotit"
                >
                  {t("profileCompletion.underageButton", "Got it")}
                </Button>
              </div>
            )}

            {ageGroup === "adult" && (
              <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                <Label>{t("profileCompletion.birthYearLabel", "What year were you born?")}</Label>
                <Select value={birthYear} onValueChange={setBirthYear}>
                  <SelectTrigger data-testid="select-birth-year">
                    <SelectValue placeholder={t("profileCompletion.birthYearPlaceholder", "Select year")} />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map((year) => (
                      <SelectItem key={year} value={year}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {birthYear && (
              <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                <Label>{t("profileCompletion.birthDateLabel", "What's your birthday?")}</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Select value={birthMonth} onValueChange={setBirthMonth}>
                    <SelectTrigger data-testid="select-birth-month">
                      <SelectValue placeholder={t("profileCompletion.birthMonthPlaceholder", "Month")} />
                    </SelectTrigger>
                    <SelectContent>
                      {monthOptions.map((m) => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={birthDay} onValueChange={setBirthDay}>
                    <SelectTrigger data-testid="select-birth-day">
                      <SelectValue placeholder={t("profileCompletion.birthDayPlaceholder", "Day")} />
                    </SelectTrigger>
                    <SelectContent>
                      {dayOptions.map((day) => (
                        <SelectItem key={day} value={day}>{day}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 pt-6 mt-auto">
        <div className="flex items-center gap-2">
          {!isFirst && (
            <Button
              variant="ghost"
              size="sm"
              onClick={goBack}
              data-testid="button-completion-back"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t("common.back")}
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!isLast && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onSkip}
              data-testid="button-completion-skip"
            >
              {t("profileCompletion.skipForNow", "Skip for now")}
            </Button>
          )}

          <Button
            onClick={goNext}
            disabled={(isLast && (savePending || !canSave)) || (!isLast && !canProceed)}
            data-testid="button-completion-next"
          >
            {isLast ? (
              savePending ? t("profileCompletion.saving") : (
                <>
                  {t("profileCompletion.saveContinue")}
                  <ArrowRight className="h-4 w-4 ml-1" />
                </>
              )
            ) : (
              <>
                {t("common.next")}
                <ChevronRight className="h-4 w-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="flex justify-center gap-1.5 pt-3">
        {MICRO_STEPS.map((step, idx) => (
          <div
            key={step}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              idx <= currentIndex ? "bg-primary w-6" : "bg-muted w-1.5"
            }`}
          />
        ))}
      </div>
    </div>
  );
});
